// src/components/SMSOutcomeModal.jsx
// Modal to log SMS conversation outcome (like CallOutcomeModal)

import React, { useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const SMSOutcomeModal = ({ isOpen, onClose, client, onSave }) => {
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const outcomes = [
    {
      id: 'satisfied',
      label: 'Customer Satisfied',
      description: 'Issue resolved, no follow-up needed',
      icon: '✅',
      color: 'green'
    },
    {
      id: 'appointment_set',
      label: 'Appointment Set',
      description: 'Scheduled a meeting or call',
      icon: '📅',
      color: 'blue'
    },
    {
      id: 'pending',
      label: 'Pending Response',
      description: 'Waiting for client reply',
      icon: '⏳',
      color: 'yellow'
    },
    {
      id: 'follow_up',
      label: 'Follow Up Needed',
      description: 'Create a task to follow up',
      icon: '🔄',
      color: 'orange'
    },
    {
      id: 'dnc',
      label: 'Unsubscribed / DNC',
      description: 'Client requested no more contact',
      icon: '🚫',
      color: 'red'
    }
  ];

  const handleSave = async () => {
    if (!selectedOutcome) return;

    setSaving(true);
    setError(null);
    try {
      // Log the SMS outcome
      const res = await fetch(`${API_BASE}/churn-prediction/sms-outcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          clientId: client?.id,
          outcome: selectedOutcome,
          notes
        })
      });

      const data = await res.json();

      if (res.ok && data.ok) {
        // Dispatch event to refresh risk score card
        window.dispatchEvent(new CustomEvent('risk-score-updated', { detail: { clientId: client?.id } }));

        // Call the onSave callback
        if (onSave) {
          onSave({ outcome: selectedOutcome, notes });
        }
        onClose();
      } else {
        setError(data.error || 'Failed to save outcome');
      }
    } catch (err) {
      console.error('Error saving SMS outcome:', err);
      setError(err.message || 'Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  if (!isOpen) return null;

  const getColorClasses = (color, isSelected) => {
    const colors = {
      green: isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300',
      blue: isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300',
      yellow: isSelected ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 hover:border-yellow-300',
      orange: isSelected ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300',
      red: isSelected ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-red-300'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleSkip}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
          <h2 className="text-lg font-semibold text-white">Log SMS Conversation</h2>
          {client && (
            <p className="text-blue-100 text-sm mt-1">
              {client.firstName} {client.lastName}
            </p>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 mb-4">How did the SMS conversation go?</p>

          {/* Outcome Options */}
          <div className="space-y-2">
            {outcomes.map((outcome) => (
              <button
                key={outcome.id}
                onClick={() => setSelectedOutcome(outcome.id)}
                className={`w-full flex items-start gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                  getColorClasses(outcome.color, selectedOutcome === outcome.id)
                }`}
              >
                <span className="text-xl">{outcome.icon}</span>
                <div className="flex-1">
                  <div className="font-medium text-gray-800">{outcome.label}</div>
                  <div className="text-sm text-gray-500">{outcome.description}</div>
                </div>
              </button>
            ))}
          </div>

          {/* Notes */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about the conversation..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>

          {/* Error Display */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
          <button
            onClick={handleSkip}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
          >
            Skip
          </button>
          <button
            onClick={handleSave}
            disabled={!selectedOutcome || saving}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              selectedOutcome && !saving
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {saving ? 'Saving...' : 'Save Outcome'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SMSOutcomeModal;
